import {
	type AppID,
	type AppState,
	PUBLIC_APP_STATE_KEYS,
	type PublicAppState,
} from "@tiendanube/nube-sdk-internal-types";

/**
 * Reduces the runtime `apps` map to what a worker may see: one entry per app
 * carrying only the keys in `PUBLIC_APP_STATE_KEYS` (`id` and `registered`).
 *
 * This is the single gate for `apps` on its way into a worker — both the
 * `__INITIAL_STATE__` injected into the bundle (`serializeInitialState`) and
 * every forwarded event (`createWorkerStateForwarder`) go through it, so an
 * app cannot read another app's script URL, config, settings, slots, cart
 * validation or errors.
 *
 * Returns `undefined` when there is nothing to send, which keeps `apps` out
 * of the serialized state entirely instead of shipping an empty object.
 */
export function toPublicApps(
	apps: Record<AppID, Partial<AppState> | undefined> | undefined,
): Record<AppID, PublicAppState> | undefined {
	if (!apps) return undefined;

	const publicApps: Record<AppID, PublicAppState> = {};

	for (const appId of Object.keys(apps)) {
		const app = apps[appId];
		if (!app) continue;

		// The map key *is* the app id, so fall back to it: handlers merge
		// partial entries (`apps: { [app]: { config } }`) into the state and
		// `id` may not be set on those branches.
		const publicApp = { id: app.id ?? appId } as Record<string, unknown>;
		for (const key of PUBLIC_APP_STATE_KEYS) {
			// Omit keys the runtime hasn't set yet rather than sending
			// `undefined`, which `JSON.stringify` would drop anyway — this keeps
			// the injected initial state and the forwarded state identical in
			// shape.
			if (app[key] === undefined) continue;
			publicApp[key] = app[key];
		}

		publicApps[appId] = publicApp as PublicAppState;
	}

	return Object.keys(publicApps).length > 0 ? publicApps : undefined;
}
